import {Component, EventEmitter, Input, Output} from '@angular/core';
import {AiNaturalLanguageQueryService} from "../../service/api/ai-natural-language-query.service";
import {
    Ambiguity,
    NaturalLanguageQueryRequest,
    NaturalLanguageQueryResponse
} from "../../model/dto/api/ai-segment-suggestion.interface";

/**
 * AI Natural Language Input Component.
 * F-024: AI-Powered Segment Suggestions - Provides a natural language interface
 * for describing desired audience segments.
 *
 * Acceptance Criteria: Given natural language input, when processed,
 * then corresponding filter criteria are generated.
 */
@Component({
    selector: 'ct-ai-natural-language-input',
    templateUrl: './ai-natural-language-input.component.html',
    standalone: false
})
export class AiNaturalLanguageInputComponent {

    @Input() targetType: string = 'CUSTOMER';
    @Input() campaignId: number;

    @Output() criteriaGenerated = new EventEmitter<{ criteriaJson: string; confidence: number }>();
    @Output() queryAccepted = new EventEmitter<number>();

    query: string = '';
    processing: boolean = false;
    response: NaturalLanguageQueryResponse = null;
    errorMessage: string = '';
    showExamples: boolean = false;

    clarifications: { [key: string]: string } = {};

    exampleQueries: string[] = [
        'Aktive Kunden in Berlin, die in den letzten 30 Tagen gekauft haben',
        'Hochwertige Verträge, die innerhalb von 90 Tagen ablaufen',
        'Kunden mit mehr als 2 Produkten und hoher Zufriedenheitsbewertung',
        'Inaktive Kunden, die sich seit 60 Tagen nicht angemeldet haben',
        'Premium-Kunden mit Upselling-Potenzial'
    ];

    constructor(private queryService: AiNaturalLanguageQueryService) {
    }

    processQuery(): void {
        if (!this.query.trim()) {
            this.errorMessage = 'Bitte geben Sie eine Beschreibung ein';
            return;
        }

        this.processing = true;
        this.errorMessage = '';
        this.response = null;

        const request: NaturalLanguageQueryRequest = {
            query: this.query,
            targetType: this.targetType,
            campaignId: this.campaignId,
            autoApplyIfHighConfidence: false
        };

        this.queryService.processQuery(request).subscribe({
            next: (response) => {
                this.response = response;
                this.processing = false;

                if (response.success && response.ambiguities?.length === 0) {
                    this.criteriaGenerated.emit({
                        criteriaJson: response.interpretedCriteriaJson,
                        confidence: response.confidenceScore
                    });
                }
            },
            error: (error) => {
                this.errorMessage = 'Fehler beim Verarbeiten der Abfrage. Bitte versuchen Sie es erneut.';
                this.processing = false;
            }
        });
    }

    provideClarification(): void {
        if (!this.response?.queryId) return;

        this.processing = true;

        this.queryService.provideClarification(this.response.queryId, this.clarifications).subscribe({
            next: (response) => {
                this.response = response;
                this.processing = false;

                if (response.success && (!response.ambiguities || response.ambiguities.length === 0)) {
                    this.criteriaGenerated.emit({
                        criteriaJson: response.interpretedCriteriaJson,
                        confidence: response.confidenceScore
                    });
                }
            },
            error: (error) => {
                this.errorMessage = 'Fehler beim Verarbeiten der Klärung. Bitte versuchen Sie es erneut.';
                this.processing = false;
            }
        });
    }

    acceptCriteria(): void {
        if (!this.response?.queryId) return;

        this.queryService.acceptQuery(this.response.queryId, false).subscribe({
            next: () => {
                this.queryAccepted.emit(this.response.queryId);
                this.reset();
            },
            error: (error) => {
                this.errorMessage = 'Fehler beim Übernehmen der Kriterien. Bitte versuchen Sie es erneut.';
            }
        });
    }

    useExample(example: string): void {
        this.query = example;
        this.showExamples = false;
    }

    reset(): void {
        this.query = '';
        this.response = null;
        this.errorMessage = '';
        this.clarifications = {};
    }

    getConfidenceColor(score: number): string {
        if (score >= 0.8) return 'text-green-600';
        if (score >= 0.6) return 'text-yellow-600';
        return 'text-red-600';
    }

    getConfidenceLabel(score: number): string {
        if (score >= 0.8) return 'Hohe Konfidenz';
        if (score >= 0.6) return 'Mittlere Konfidenz';
        return 'Niedrige Konfidenz';
    }

    selectClarification(ambiguity: Ambiguity, suggestion: string): void {
        this.clarifications[ambiguity.term] = suggestion;
    }

    hasClarifications(): boolean {
        return Object.keys(this.clarifications).length > 0;
    }
}
